import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { IVisibleLogic } from 'config'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { getAdminConfig, setAdminConfig } from 'services'
import { Button, InputVisibleLogic, Toggle } from 'stories/components'
import { confirm } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { IOverviewStepTitleConfig, IOverviewStepTitleRule } from '../RulesConfiguration/OverviewStepTitles'
import { borrowerPipelineAttentionTitleKeys, TBorrowerPipelineAttentionTitlesConfig } from './types'

const newRule = (): IOverviewStepTitleRule => ({
  title: '',
  visibleLogic: [],
})

const ensureConfig = (data: TBorrowerPipelineAttentionTitlesConfig, key: string): IOverviewStepTitleConfig => {
  if (!data[key]) data[key] = { default: '', rules: [] }
  if (!Array.isArray(data[key].rules)) data[key].rules = []
  return data[key]
}

export const BorrowerPipelineAttentionTitles = () => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<TBorrowerPipelineAttentionTitlesConfig>({})
  const [changed, setChanged] = useState(false)

  useEffect(() => {
    initData()
  }, [])

  const initData = async () => {
    setLoading(true)
    try {
      const value = await getAdminConfig('borrowerPipelineAttentionTitles')
      setData(value || {})
      setChanged(false)
    } finally {
      setLoading(false)
    }
  }

  const onSubmit = async () => {
    setLoading(true)
    try {
      await setAdminConfig('borrowerPipelineAttentionTitles', data)
      toast('Successfully submitted', { type: 'success' })
      setChanged(false)
    } finally {
      setLoading(false)
    }
  }

  const update = (mutator: (next: TBorrowerPipelineAttentionTitlesConfig) => void) => {
    const next = cloneDeep(data)
    mutator(next)
    setData(next)
    setChanged(true)
  }

  const onChangeDefault = (key: string, value: string) => {
    update((next) => {
      ensureConfig(next, key).default = value
    })
  }

  const onChangeShow = (key: string, value: boolean) => {
    update((next) => {
      ensureConfig(next, key).show = value
    })
  }

  const onAddRule = (key: string) => {
    update((next) => {
      ensureConfig(next, key).rules.push(newRule())
    })
  }

  const onRemoveRule = async (key: string, ruleIndex: number) => {
    const ok = await confirm('Are you sure you want to remove this rule?')
    if (!ok) return
    update((next) => {
      ensureConfig(next, key).rules.splice(ruleIndex, 1)
    })
  }

  const onChangeRuleTitle = (key: string, ruleIndex: number, value: string) => {
    update((next) => {
      const cfg = ensureConfig(next, key)
      if (!cfg.rules[ruleIndex]) return
      cfg.rules[ruleIndex].title = value
    })
  }

  const onChangeRuleLogic = (key: string, ruleIndex: number, logic: IVisibleLogic[][]) => {
    update((next) => {
      const cfg = ensureConfig(next, key)
      if (!cfg.rules[ruleIndex]) return
      cfg.rules[ruleIndex].visibleLogic = logic
    })
  }

  const renderRule = (itemKey: string, rule: IOverviewStepTitleRule, ruleIndex: number) => {
    const ruleKey = `${itemKey}-rule-${ruleIndex}`
    return (
      <div className="border rounded-md p-3 mb-3 bg-white" key={ruleKey}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold">Rule {ruleIndex + 1}</p>
          <span className="btn-icon" onClick={() => onRemoveRule(itemKey, ruleIndex)}>
            <TrashIcon className="w-4 h-4 text-red-500" />
          </span>
        </div>

        <div className="mb-3">
          <RenderInput
            Key={`${ruleKey}-title`}
            input={{
              inputType: 'text',
              title: 'Title (used when conditions below match)',
              value: rule.title,
              required: true,
            }}
            onChange={(_k: string, value: string) => onChangeRuleTitle(itemKey, ruleIndex, value)}
          />
        </div>

        <InputVisibleLogic
          id={`${ruleKey}-logic`}
          title="Visible Conditions"
          value={rule.visibleLogic || []}
          options={{ hasNotVisible: true }}
          onChange={(value: IVisibleLogic[][]) => onChangeRuleLogic(itemKey, ruleIndex, value)}
        />
      </div>
    )
  }

  const renderItem = (itemKey: string, itemLabel: string) => {
    const cfg: IOverviewStepTitleConfig = data[itemKey] || { default: '', rules: [] }
    const rules = cfg.rules || []
    return (
      <div className="mb-8" key={itemKey}>
        <RenderInput
          Key={`${itemKey}-section`}
          input={{
            inputType: 'section',
            title: itemLabel,
            additionalElements: (
              <div>
                <Toggle
                  id={`${itemKey}-toggle`}
                  title="Show in Items that Require Attention"
                  value={cfg.show || cfg.show === undefined}
                  onChange={(value: boolean) => onChangeShow(itemKey, value)}
                />
              </div>
            ),
          }}
          onChange={() => {}}
        />

        <div className="mt-4 pl-4">
          <div className="mb-4 max-w-xl">
            <RenderInput
              Key={`${itemKey}-default`}
              input={{
                inputType: 'text',
                title: `Default Title (falls back to "${itemLabel}" when empty)`,
                value: cfg.default || '',
              }}
              onChange={(_k: string, value: string) => onChangeDefault(itemKey, value)}
            />
          </div>

          <div className="bg-slate-50 p-3 rounded-md">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold">
                Conditional Titles
                <span className="text-xs text-gray-500 ml-2 font-normal">
                  The first rule whose conditions match wins. If no rule matches, the Default Title above is used.
                </span>
              </p>
              <span className="btn-icon" onClick={() => onAddRule(itemKey)}>
                <PlusIcon className="w-4 h-4" />
              </span>
            </div>

            {rules.length === 0 && (
              <p className="text-sm text-gray-500 italic">No conditional rules. Click the + above to add one.</p>
            )}

            {rules.map((rule, ruleIndex) => renderRule(itemKey, rule, ruleIndex))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="relative px-3 py-1.5 min-h-[100vh]">
      <LayoutLoading show={loading} />

      <div className="text-xl font-bold mb-4">Borrower Pipeline Attention Titles</div>
      <p className="text-sm text-gray-600 mb-6 max-w-3xl">
        Configure titles shown in the borrower loan pipeline and loan overview under &quot;Items that Require
        Attention&quot;. For each item, you can define conditional titles using the same visibility logic as Loan Field
        Properties. The first matching rule wins; otherwise the Default Title is used (falling back to the system
        default when empty). Use the toggle to hide an item from the attention list entirely.
      </p>

      {Object.keys(borrowerPipelineAttentionTitleKeys).map((key) =>
        renderItem(key, borrowerPipelineAttentionTitleKeys[key]),
      )}

      <div className="flex justify-center mt-4">
        <Button onClick={onSubmit} loading={loading} disabled={!changed}>
          Save Changes
        </Button>
      </div>
      <SaveChanges show={changed} label="Save Changes" onSave={onSubmit} />
    </div>
  )
}
